// src/components/profile/ProfileCard.jsx
import React, { useContext, useEffect } from "react";
import { CameraIcon, CheckCircleIcon } from "../common/IconsComponent";
import RatingStars from "../ui/RatingStars";
import { useUser } from "../../contexts/UserContext";
import { DocumentsContext } from "../../contexts/DocumentsContext";
import { useAvailability } from "../../contexts/AvailabilityContext";

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_MAP = {
  APPROVED: { label: "מאושר",          cls: "bg-green-100 text-green-800", showIcon: true  },
  PENDING:  { label: "ממתין לאישור",   cls: "bg-amber-100 text-amber-900", showIcon: false },
  REJECTED: { label: "נדחה",           cls: "bg-red-100 text-red-800",     showIcon: false },
  BLOCKED:  { label: "חסום",           cls: "bg-gray-200 text-gray-800",   showIcon: false },
  // supplier_profiles.status uses SUSPENDED (not BLOCKED) — without this a
  // suspended learning center showed "לא מוגדר".
  SUSPENDED: { label: "מושעה",         cls: "bg-gray-200 text-gray-800",   showIcon: false },
};

const STATUS_DEFAULT = { label: "לא מוגדר", cls: "bg-gray-100 text-gray-600", showIcon: false };

// ─── Component ───────────────────────────────────────────────────────────────

const ProfileCard = ({ scrollToSection }) => {
  const { user, refreshMe }    = useUser();
  const { documents, documentTypes } = useContext(DocumentsContext);
  const { savedAvailability }  = useAvailability();

  useEffect(() => { refreshMe?.(); }, []);

  const role = user.role || "student";
  const isTeacher = role === "teacher";
  const isSupplier = role === "supplier";

  // A supplier's display name is the business name (supplier_name),
  // not firstName/lastName — they're never set for that role at all
  // (see PersonalInfoSection.jsx).
  const displayName = isSupplier ? (user.businessName || "") : `${user.firstName || ""} ${user.lastName || ""}`.trim();
  const initials = (isSupplier ? user.businessName?.charAt(0) : user.firstName?.charAt(0)) || "א";

  // Completion checks — same role-aware logic as Sidebar.js, since
  // "personal info complete" and "which documents are required" both
  // mean something different for a supplier than for a teacher.
  const isPersonalCompleted = isSupplier
    ? Boolean(
        user.businessName && user.contactPerson &&
        user.email && Number(user.emailConfirmed) === 1 &&
        user.phone && Number(user.phoneConfirmed) === 1
      )
    : Boolean(
        user.firstName && user.lastName &&
        user.email && Number(user.emailConfirmed) === 1 &&
        user.phone && Number(user.phoneConfirmed) === 1 &&
        (user.aboutMe || "").length >= 20
      );

  // documentTypes is already the correct per-role list — the
  // DocumentsProvider itself computes this from effectiveRole
  // (TEACHER_DOCUMENT_TYPES / SUPPLIER_DOCUMENT_TYPES), so there's no
  // need to re-derive it here.
  const isDocumentsCompleted = documentTypes.every((type) =>
    (Array.isArray(documents) ? documents : []).some((d) => d?.type === type)
  );

  const isAvailabilityCompleted =
    Array.isArray(savedAvailability) &&
    savedAvailability.some((day) => Array.isArray(day) && day.some(Boolean));

  const isZoomCompleted = Boolean(
    user.zoomAccountId && user.zoomClientId && user.zoomSecretKey
  );

  // A supplier has a real documents tab too (Profile.js's
  // canUseDocuments = isTeacher || isSupplier) — it just wasn't being
  // counted here at all before, so their completion % was always
  // missing that piece of real progress.
  const tasks = isTeacher
    ? [isPersonalCompleted, isDocumentsCompleted, isAvailabilityCompleted, isZoomCompleted]
    : isSupplier
    ? [isPersonalCompleted, isDocumentsCompleted]
    : [isPersonalCompleted];

  const completedTasks  = tasks.filter(Boolean).length;
  const completionPct   = tasks.length ? Math.round((completedTasks / tasks.length) * 100) : 0;
  const remaining       = tasks.length - completedTasks;

  // A supplier's approval status lives on a different field
  // (supplier_status, from supplier_profiles) than a teacher's
  // (teacher_status, directly on users) — using teacher_status
  // unconditionally meant a supplier always showed "לא מוגדר"
  // regardless of their real approval state.
  const statusView = isSupplier
    ? (STATUS_MAP[user.supplier_status] ?? STATUS_DEFAULT)
    : (STATUS_MAP[user.teacher_status] ?? STATUS_DEFAULT);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-4 sm:mb-6">

      {/* Cover + avatar */}
      <div className="relative h-24 sm:h-32 bg-gradient-to-r from-orange-50 to-orange-200">
        <div className="absolute -bottom-12 sm:-bottom-16 inset-x-0 flex justify-center">
          <div className="relative">
            <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-blue-100">
              {user.picture ? (
                <img src={user.picture} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-blue-500 text-3xl sm:text-4xl font-bold">
                  {initials}
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={() => scrollToSection("photo")}
              className="absolute bottom-0 right-0 bg-blue-600 text-white p-1.5 sm:p-2 rounded-full shadow-md hover:bg-blue-700 transition-colors"
              aria-label="שנה תמונת פרופיל"
            >
              <CameraIcon size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Name + info */}
      <div className="pt-14 sm:pt-20 pb-5 sm:pb-6 px-4 sm:px-6 text-center" dir="rtl">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900">
          {displayName}
        </h2>
        {!isSupplier && user.aboutMe && (
          <p className="text-gray-500 text-sm mt-2 sm:mt-4 line-clamp-3">{user.aboutMe}</p>
        )}

        {isTeacher && (
          <div className="mt-3 sm:mt-4 flex flex-col items-center gap-3">
            <div className="inline-flex items-center justify-center whitespace-nowrap">
              <RatingStars value={user.rating} size={15} />
            </div>
            <span className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${statusView.cls}`}>
              <span className="font-bold">{statusView.label}</span>
              {statusView.showIcon && <CheckCircleIcon size={12} />}
            </span>
          </div>
        )}

        {isSupplier && (
          <div className="mt-3 sm:mt-4 flex flex-col items-center gap-3">
            <div className="inline-flex items-center justify-center gap-2 whitespace-nowrap">
              <RatingStars value={user.supplierRating} size={15} />
              <span className="text-xs text-gray-500">
                {user.supplierRatingCount > 0
                  ? `(${user.supplierRatingCount} דירוגים)`
                  : "אין דירוגים עדיין"}
              </span>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full inline-flex items-center gap-1.5 ${statusView.cls}`}>
              <span className="font-bold">{statusView.label}</span>
              {statusView.showIcon && <CheckCircleIcon size={12} />}
            </span>
            {String(user.supplier_admin_notes || '').trim() && (
              <div className="w-full max-w-md text-right bg-gray-50 border border-gray-200 rounded-xl p-3" dir="rtl">
                <p className="text-xs font-bold text-gray-700 mb-1">הערת הפלטפורמה</p>
                <p className="text-sm text-gray-600 whitespace-pre-line">{user.supplier_admin_notes}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Completion bar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-gray-500 text-xs mb-1">השלמת פרופיל</p>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
            <div
              className="bg-gradient-to-r from-blue-600 to-indigo-500 h-2 rounded-full transition-all duration-500"
              style={{ width: `${completionPct}%` }}
            />
          </div>
          <div className="text-xs text-gray-600 flex justify-between">
            <span>{completionPct}%</span>
            <span>{remaining} פריטים להשלמה</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileCard;